import type { ReactNode } from 'react';
import { clsx } from '../../../../ds/clsx';
import { eur, type LedgerKey, type LedgerRow } from '../model';
import styles from '../advisor.module.css';

/** One section of the advisor view: 24 px display heading, optional action on the right. */
export function Section({
  id,
  title,
  aside,
  children,
}: {
  id: string;
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.section} aria-labelledby={id}>
      <div className={styles.sectionHead}>
        <h2 id={id} className={styles.heading}>
          {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

export type BadgeTone = 'dark' | 'blue' | 'green';

const BADGE: Record<BadgeTone, string | undefined> = {
  dark: styles.badgeDark,
  blue: styles.badgeBlue,
  green: styles.badgeGreen,
};

export function Badge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  return <span className={clsx(styles.badge, BADGE[tone])}>{children}</span>;
}

/** Label, value and an optional caption stacked (tiles, the four financing figures). */
export function Figure({
  label,
  value,
  caption,
  small = false,
  className,
}: {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  small?: boolean;
  className?: string | undefined;
}) {
  return (
    <div className={clsx(styles.pair, className)}>
      <span className={styles.label}>{label}</span>
      <span className={small ? styles.valueSmall : styles.value}>{value}</span>
      {caption ? <span className={styles.caption}>{caption}</span> : null}
    </div>
  );
}

/** One `dl` row: term left, value (+ muted hint) right. */
export function DlRow({
  term,
  value,
  hint,
  total = false,
}: {
  term: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  total?: boolean;
}) {
  return (
    <div className={clsx(styles.dlRow, total && styles.dlTotal)}>
      <dt>{term}</dt>
      <dd>
        {hint ? <span className={styles.dlHint}>{hint}</span> : null}
        {value}
      </dd>
    </div>
  );
}

/** Colour of a ledger row (brief: Kaltmiete navy-500 … Überschuss green-500). */
export const LEDGER_TONE: Record<LedgerKey, string | undefined> = {
  rent: styles.toneRent,
  taxBenefit: styles.toneTaxBenefit,
  ownShare: styles.toneOwnShare,
  principal: styles.tonePrincipal,
  interest: styles.toneInterest,
  costs: styles.toneCosts,
  tax: styles.toneTax,
  surplus: styles.toneSurplus,
};

/** A ledger column as `dl` with colour swatches and the „Summe" row. */
export function LedgerList({
  title,
  rows,
  totalCents,
}: {
  title: string;
  rows: LedgerRow[];
  totalCents: number;
}) {
  return (
    <div>
      <h3 className={styles.subHeading}>{title}</h3>
      <dl className={styles.dl}>
        {rows.map((row) => (
          <DlRow
            key={row.key}
            term={
              <span className={styles.swatchLabel}>
                <span className={clsx(styles.swatch, LEDGER_TONE[row.key])} aria-hidden="true" />
                {row.label}
              </span>
            }
            value={eur(row.cents)}
          />
        ))}
        <DlRow term="Summe" value={eur(totalCents)} total />
      </dl>
    </div>
  );
}
